import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Users, Building2, Download, CheckCircle2, XCircle,
  ZoomIn, ZoomOut, RotateCcw, Move, Plus, Upload
} from 'lucide-react';
import AddIndividualModal from '@/components/modals/AddIndividualModal';
import AddEntityModal from '@/components/modals/AddEntityModal';
import { toast } from 'sonner';

// Get color based on family role
const getFamilyRoleColor = (role) => {
  if (!role) return '#8C8078';
  const r = role.toLowerCase();
  if (r.includes('patriarch')) return '#4A3B32';
  if (r.includes('matriarch')) return '#7D6B5C';
  if (r.includes('spouse') || r.includes('partner')) return '#D99A6C';
  if (r.includes('son') || r.includes('daughter')) return '#6B8E6B';
  if (r.includes('grandson') || r.includes('granddaughter')) return '#5C7D9E';
  if (r.includes('sibling')) return '#8B7355';
  return '#8C8078';
};

// Get color based on entity type
const getEntityTypeColor = (type) => {
  switch (type) {
    case 'Company': return '#4A3B32';
    case 'Trust': return '#6B8E6B';
    case 'SMSF': return '#D99A6C';
    case 'Foundation': return '#7D8C9E';
    default: return '#8C8078';
  }
};

// Interactive SVG Family Tree Component with Zoom/Pan
function FamilyTreeSVG({ individuals, onSelectPerson }) {
  const containerRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Group individuals by generation
  const generations = useMemo(() => {
    const gens = {};
    individuals.forEach(ind => {
      const gen = ind.generation || 2;
      if (!gens[gen]) gens[gen] = [];
      gens[gen].push(ind);
    });
    return gens;
  }, [individuals]);

  const genKeys = Object.keys(generations).sort((a, b) => a - b);
  
  // Calculate SVG dimensions
  const maxPerGen = Math.max(...genKeys.map(k => generations[k].length), 1);
  const svgWidth = Math.max(800, maxPerGen * 180 + 100);
  const svgHeight = Math.max(400, genKeys.length * 160 + 100);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: Math.min(prev.scale * 1.2, 3) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: Math.max(prev.scale / 1.2, 0.3) }));
  }, []);

  const handleReset = useCallback(() => {
    setTransform({ x: 0, y: 0, scale: 1 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setTransform(prev => ({
      ...prev,
      scale: Math.min(Math.max(prev.scale * delta, 0.3), 3)
    }));
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('g[data-clickable="true"]')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
  }, [transform.x, transform.y]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setTransform(prev => ({
      ...prev,
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    }));
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Calculate positions and relationships
  const { positions, lines } = useMemo(() => {
    const pos = {};
    const connectionLines = [];

    // Calculate positions for all individuals
    genKeys.forEach((gen, genIndex) => {
      const genIndividuals = generations[gen];
      const startX = (svgWidth - (genIndividuals.length * 160)) / 2 + 80;
      genIndividuals.forEach((ind, idx) => {
        pos[ind.id] = {
          x: startX + idx * 160,
          y: genIndex * 150 + 60
        };
      });
    });

    // Find spouse pairs in same generation
    const processedSpouses = new Set();
    individuals.forEach(ind => {
      const indPos = pos[ind.id];
      if (!indPos) return;
      
      ind.relationships?.forEach(rel => {
        if (rel.type === 'Spouse' && !processedSpouses.has(`${ind.id}-${rel.individualId}`) && !processedSpouses.has(`${rel.individualId}-${ind.id}`)) {
          const spousePos = pos[rel.individualId];
          if (spousePos) {
            processedSpouses.add(`${ind.id}-${rel.individualId}`);
            connectionLines.push({
              key: `spouse-${ind.id}-${rel.individualId}`,
              type: 'spouse',
              x1: Math.min(indPos.x, spousePos.x) + 55,
              y1: indPos.y + 40,
              x2: Math.max(indPos.x, spousePos.x) - 55,
              y2: spousePos.y + 40
            });
          }
        }
      });
    });

    // Find parent-child relationships based on generation
    genKeys.forEach((gen, genIndex) => {
      if (genIndex === 0) return; // First generation has no parents in tree
      
      const currentGen = generations[gen];
      const prevGen = generations[genKeys[genIndex - 1]];
      
      if (!prevGen) return;
      
      currentGen.forEach(child => {
        const childPos = pos[child.id];
        if (!childPos) return;
        
        // Check explicit relationships
        let hasExplicitParent = false;
        child.relationships?.forEach(rel => {
          if (rel.type === 'Child' || rel.type === 'Parent') {
            const parentPos = pos[rel.individualId];
            if (parentPos && parentPos.y < childPos.y) {
              hasExplicitParent = true;
              connectionLines.push({
                key: `parent-${child.id}-${rel.individualId}`,
                type: 'parent-child',
                x1: parentPos.x,
                y1: parentPos.y + 80,
                x2: childPos.x,
                y2: childPos.y
              });
            }
          }
        });
        
        // If no explicit parent, connect to center of previous generation
        if (!hasExplicitParent && prevGen.length > 0) {
          // Find the center point of the previous generation
          const prevGenXs = prevGen.map(p => pos[p.id]?.x || 0);
          const centerX = (Math.min(...prevGenXs) + Math.max(...prevGenXs)) / 2;
          const centerY = pos[prevGen[0].id]?.y || 0;
          
          connectionLines.push({
            key: `gen-${child.id}`,
            type: 'generation',
            x1: centerX,
            y1: centerY + 80,
            x2: childPos.x,
            y2: childPos.y
          });
        }
      });
    });

    return { positions: pos, lines: connectionLines };
  }, [individuals, generations, genKeys, svgWidth]);

  return (
    <div className="relative">
      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-md border border-[#E8E4DF]">
        <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0 hover:bg-[#F0EBE6]" title="Zoom In">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0 hover:bg-[#F0EBE6]" title="Zoom Out">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <div className="w-full h-px bg-[#E8E4DF]" />
        <Button variant="ghost" size="sm" onClick={handleReset} className="h-8 w-8 p-0 hover:bg-[#F0EBE6]" title="Reset View">
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>

      <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border border-[#E8E4DF]">
        <Move className="w-4 h-4 text-[#8C8078]" />
        <span className="text-xs text-[#8C8078]">Drag to pan • Scroll to zoom</span>
      </div>

      <div className="absolute bottom-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1 shadow-md border border-[#E8E4DF]">
        <span className="text-xs text-[#5D524D] font-medium">{Math.round(transform.scale * 100)}%</span>
      </div>

      <div 
        ref={containerRef}
        className="overflow-hidden rounded-lg border border-[#E8E4DF] bg-[#FDFCFB]"
        style={{ height: '500px', cursor: isDragging ? 'grabbing' : 'grab' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <svg 
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
          data-testid="family-tree-svg"
        >
          {/* Generation Labels */}
          {genKeys.map((gen, idx) => (
            <text key={`gen-label-${gen}`} x="20" y={idx * 150 + 90} className="fill-[#8C8078] text-xs font-medium">
              Gen {gen}
            </text>
          ))}

          {/* Connection Lines */}
          {lines.map(line => {
            if (line.type === 'spouse') {
              return (
                <line
                  key={line.key}
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  stroke="#D99A6C"
                  strokeWidth="2"
                  strokeDasharray="6,3"
                />
              );
            } else if (line.type === 'parent-child') {
              return (
                <path
                  key={line.key}
                  d={`M ${line.x1} ${line.y1} 
                      L ${line.x1} ${(line.y1 + line.y2) / 2} 
                      L ${line.x2} ${(line.y1 + line.y2) / 2} 
                      L ${line.x2} ${line.y2}`}
                  fill="none"
                  stroke="#4A3B32"
                  strokeWidth="2"
                />
              );
            } else {
              // Generation connection (dotted)
              return (
                <path
                  key={line.key}
                  d={`M ${line.x1} ${line.y1} 
                      L ${line.x1} ${(line.y1 + line.y2) / 2} 
                      L ${line.x2} ${(line.y1 + line.y2) / 2} 
                      L ${line.x2} ${line.y2}`}
                  fill="none"
                  stroke="#8C8078"
                  strokeWidth="1.5"
                  strokeDasharray="4,4"
                  opacity="0.6"
                />
              );
            }
          })}

          {/* Individual Nodes */}
          {genKeys.map((gen) => {
            return generations[gen].map((ind) => {
              const pos = positions[ind.id];
              if (!pos) return null;
              const roleColor = getFamilyRoleColor(ind.familyRole || ind.role);
              const displayRole = ind.familyRole || ind.role || 'Family Member';
              
              return (
                <g 
                  key={ind.id} 
                  transform={`translate(${pos.x - 55}, ${pos.y})`}
                  onClick={(e) => { e.stopPropagation(); onSelectPerson(ind); }}
                  style={{ cursor: 'pointer' }}
                  data-clickable="true"
                  data-testid={`tree-node-${ind.id}`}
                >
                  <rect x="0" y="0" width="110" height="80" rx="8" fill="white" stroke={roleColor} strokeWidth="2" />
                  <rect x="0" y="0" width="110" height="8" rx="8" ry="0" fill={roleColor} />
                  <text x="55" y="32" textAnchor="middle" className="fill-[#2C2420] text-sm font-medium">
                    {ind.preferredName || ind.firstName}
                  </text>
                  <text x="55" y="48" textAnchor="middle" className="fill-[#2C2420] text-sm">
                    {ind.lastName}
                  </text>
                  <text x="55" y="68" textAnchor="middle" className="fill-[#8C8078] text-xs">
                    {displayRole.length > 14 ? displayRole.substring(0, 14) + '...' : displayRole}
                  </text>
                  {/* Corporate roles indicator */}
                  {ind.corporateRoles?.length > 0 && (
                    <g transform="translate(90, 60)">
                      <circle cx="0" cy="0" r="8" fill="#6B8E6B" />
                      <text x="0" y="4" textAnchor="middle" className="fill-white text-xs font-bold">
                        {ind.corporateRoles.length}
                      </text>
                    </g>
                  )}
                </g>
              );
            });
          })}

          {/* Legend */}
          <g transform={`translate(${svgWidth - 180}, ${svgHeight - 90})`}>
            <rect x="0" y="0" width="170" height="80" rx="8" fill="#F9F7F5" stroke="#E8E4DF" />
            <text x="10" y="18" className="fill-[#5D524D] text-xs font-medium">Legend</text>
            <line x1="10" y1="32" x2="40" y2="32" stroke="#4A3B32" strokeWidth="2" />
            <text x="50" y="35" className="fill-[#8C8078] text-xs">Parent-Child</text>
            <line x1="10" y1="50" x2="40" y2="50" stroke="#D99A6C" strokeWidth="2" strokeDasharray="6,3" />
            <text x="50" y="53" className="fill-[#8C8078] text-xs">Spouse/Partner</text>
            <line x1="10" y1="68" x2="40" y2="68" stroke="#8C8078" strokeWidth="1.5" strokeDasharray="4,4" />
            <text x="50" y="71" className="fill-[#8C8078] text-xs">Generation</text>
          </g>
        </svg>
      </div>
    </div>
  );
}

// Entity Relationship SVG Component with Zoom/Pan
function EntityRelationshipSVG({ entities, individuals, onSelectEntity }) {
  const containerRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Create individual lookup
  const individualMap = useMemo(() => {
    const map = {};
    individuals.forEach(ind => {
      map[ind.id] = ind;
      map[`${ind.firstName} ${ind.lastName}`] = ind;
    });
    return map;
  }, [individuals]);

  // Group entities by type
  const entityTypes = useMemo(() => {
    const types = {};
    entities.forEach(ent => {
      const type = ent.type || 'Other';
      if (!types[type]) types[type] = [];
      types[type].push(ent);
    });
    return types;
  }, [entities]);

  const typeOrder = ['Company', 'Trust', 'SMSF', 'Foundation', 'Other'];
  const orderedTypes = typeOrder.filter(t => entityTypes[t]);
  
  // Calculate SVG dimensions
  const maxPerType = Math.max(...orderedTypes.map(t => entityTypes[t]?.length || 0), 1);
  const svgWidth = Math.max(900, maxPerType * 200 + 100);
  const svgHeight = Math.max(400, orderedTypes.length * 140 + 150);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: Math.min(prev.scale * 1.2, 3) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: Math.max(prev.scale / 1.2, 0.3) }));
  }, []);

  const handleReset = useCallback(() => {
    setTransform({ x: 0, y: 0, scale: 1 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setTransform(prev => ({
      ...prev,
      scale: Math.min(Math.max(prev.scale * delta, 0.3), 3)
    }));
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('g[data-clickable="true"]')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
  }, [transform.x, transform.y]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setTransform(prev => ({
      ...prev,
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    }));
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Calculate positions and relationships
  const { positions, lines } = useMemo(() => {
    const pos = {};
    const connectionLines = [];

    // Calculate positions for entities
    orderedTypes.forEach((type, typeIndex) => {
      const typeEntities = entityTypes[type] || [];
      const startX = (svgWidth - (typeEntities.length * 180)) / 2 + 90;
      typeEntities.forEach((ent, idx) => {
        pos[ent.id] = {
          x: startX + idx * 180,
          y: typeIndex * 130 + 60
        };
        pos[ent.name] = pos[ent.id]; // Also map by name
      });
    });

    // Build trustee/director relationships
    entities.forEach(ent => {
      const entPos = pos[ent.id];
      if (!entPos) return;

      // For Trusts/SMSFs - connect to corporate trustees
      if (ent.trusteeType === 'Corporate' && ent.trustees) {
        ent.trustees.forEach(trustee => {
          // Try to find the trustee as an entity
          const trusteeEntity = entities.find(e => 
            e.name === trustee.name || 
            e.acn === trustee.acnAbn || 
            e.abn === trustee.acnAbn
          );
          if (trusteeEntity && pos[trusteeEntity.id]) {
            const trusteePos = pos[trusteeEntity.id];
            connectionLines.push({
              key: `trustee-${ent.id}-${trusteeEntity.id}`,
              type: 'trustee',
              x1: trusteePos.x,
              y1: trusteePos.y + 70,
              x2: entPos.x,
              y2: entPos.y,
              label: 'Trustee'
            });
          }
        });
      }

      // For Companies - show shareholder relationships to other entities
      if (ent.type === 'Company' && ent.shareholders) {
        ent.shareholders.forEach(sh => {
          // Check if shareholder is another entity
          const shareholderEntity = entities.find(e => e.name === sh.name && e.id !== ent.id);
          if (shareholderEntity && pos[shareholderEntity.id]) {
            const shPos = pos[shareholderEntity.id];
            connectionLines.push({
              key: `shareholder-${ent.id}-${shareholderEntity.id}`,
              type: 'shareholder',
              x1: shPos.x,
              y1: shPos.y + 70,
              x2: entPos.x,
              y2: entPos.y,
              label: `${sh.ownershipPercentage || '?'}%`
            });
          }
        });
      }
    });

    return { positions: pos, lines: connectionLines };
  }, [entities, entityTypes, orderedTypes, svgWidth]);

  return (
    <div className="relative">
      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-md border border-[#E8E4DF]">
        <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0 hover:bg-[#F0EBE6]" title="Zoom In">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0 hover:bg-[#F0EBE6]" title="Zoom Out">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <div className="w-full h-px bg-[#E8E4DF]" />
        <Button variant="ghost" size="sm" onClick={handleReset} className="h-8 w-8 p-0 hover:bg-[#F0EBE6]" title="Reset View">
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>

      <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border border-[#E8E4DF]">
        <Move className="w-4 h-4 text-[#8C8078]" />
        <span className="text-xs text-[#8C8078]">Drag to pan • Scroll to zoom</span>
      </div>

      <div className="absolute bottom-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1 shadow-md border border-[#E8E4DF]">
        <span className="text-xs text-[#5D524D] font-medium">{Math.round(transform.scale * 100)}%</span>
      </div>

      <div 
        ref={containerRef}
        className="overflow-hidden rounded-lg border border-[#E8E4DF] bg-[#FDFCFB]"
        style={{ height: '500px', cursor: isDragging ? 'grabbing' : 'grab' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <svg 
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
          data-testid="entity-relationship-svg"
        >
          {/* Type Labels */}
          {orderedTypes.map((type, idx) => (
            <g key={`type-label-${type}`}>
              <rect x="10" y={idx * 130 + 50} width="70" height="24" rx="4" fill={getEntityTypeColor(type)} opacity="0.2" />
              <text x="45" y={idx * 130 + 67} textAnchor="middle" className="fill-[#5D524D] text-xs font-medium">
                {type}
              </text>
            </g>
          ))}

          {/* Connection Lines */}
          {lines.map(line => (
            <g key={line.key}>
              <path
                d={`M ${line.x1} ${line.y1} 
                    C ${line.x1} ${line.y1 + 40}, ${line.x2} ${line.y2 - 40}, ${line.x2} ${line.y2}`}
                fill="none"
                stroke={line.type === 'trustee' ? '#6B8E6B' : '#4A3B32'}
                strokeWidth="2"
                strokeDasharray={line.type === 'trustee' ? '6,3' : 'none'}
                opacity="0.7"
                markerEnd="url(#arrowhead)"
              />
              {line.label && (
                <text
                  x={(line.x1 + line.x2) / 2}
                  y={(line.y1 + line.y2) / 2}
                  textAnchor="middle"
                  className="fill-[#5D524D] text-xs font-medium"
                  style={{ backgroundColor: 'white' }}
                >
                  {line.label}
                </text>
              )}
            </g>
          ))}

          {/* Arrow marker definition */}
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#4A3B32" opacity="0.7" />
            </marker>
          </defs>

          {/* Entity Nodes */}
          {orderedTypes.map((type) => {
            return (entityTypes[type] || []).map((ent) => {
              const pos = positions[ent.id];
              if (!pos) return null;
              const typeColor = getEntityTypeColor(ent.type);
              
              // Get subtype display
              const subtype = ent.companySubtype || ent.trustSubtype || ent.foundationStructure || '';
              
              // Count linked people
              const linkedCount = (ent.directors?.length || 0) + (ent.shareholders?.length || 0) + 
                                 (ent.trustees?.length || 0) + (ent.members?.length || 0) +
                                 (ent.responsiblePersons?.length || 0);
              
              return (
                <g 
                  key={ent.id} 
                  transform={`translate(${pos.x - 75}, ${pos.y})`}
                  onClick={(e) => { e.stopPropagation(); onSelectEntity(ent); }}
                  style={{ cursor: 'pointer' }}
                  data-clickable="true"
                  data-testid={`entity-node-${ent.id}`}
                >
                  <rect x="0" y="0" width="150" height="70" rx="8" fill="white" stroke={typeColor} strokeWidth="2" />
                  <rect x="0" y="0" width="150" height="8" rx="8" ry="0" fill={typeColor} />
                  <text x="75" y="28" textAnchor="middle" className="fill-[#2C2420] text-xs font-medium">
                    {ent.name?.length > 20 ? ent.name.substring(0, 20) + '...' : ent.name}
                  </text>
                  {subtype && (
                    <text x="75" y="42" textAnchor="middle" className="fill-[#8C8078] text-xs">
                      {subtype}
                    </text>
                  )}
                  <g transform="translate(10, 50)">
                    {linkedCount > 0 && (
                      <>
                        <Users className="w-3 h-3" style={{ color: '#8C8078' }} />
                        <text x="18" y="10" className="fill-[#8C8078] text-xs">{linkedCount}</text>
                      </>
                    )}
                  </g>
                  <g transform="translate(110, 50)">
                    {ent.authoritySet ? (
                      <CheckCircle2 className="w-4 h-4" style={{ color: '#6B8E6B' }} />
                    ) : (
                      <XCircle className="w-4 h-4" style={{ color: '#C25B5B' }} />
                    )}
                  </g>
                </g>
              );
            });
          })}

          {/* Legend */}
          <g transform={`translate(${svgWidth - 200}, ${svgHeight - 100})`}>
            <rect x="0" y="0" width="190" height="90" rx="8" fill="#F9F7F5" stroke="#E8E4DF" />
            <text x="10" y="18" className="fill-[#5D524D] text-xs font-medium">Legend</text>
            <line x1="10" y1="35" x2="50" y2="35" stroke="#4A3B32" strokeWidth="2" opacity="0.7" />
            <text x="60" y="38" className="fill-[#8C8078] text-xs">Ownership</text>
            <line x1="10" y1="55" x2="50" y2="55" stroke="#6B8E6B" strokeWidth="2" strokeDasharray="6,3" opacity="0.7" />
            <text x="60" y="58" className="fill-[#8C8078] text-xs">Trustee Relationship</text>
            <g transform="translate(10, 68)">
              <CheckCircle2 className="w-3 h-3" style={{ color: '#6B8E6B' }} />
              <text x="20" y="10" className="fill-[#8C8078] text-xs">Auth Set</text>
              <XCircle className="w-3 h-3" style={{ color: '#C25B5B', transform: 'translateX(80px)' }} />
              <text x="100" y="10" className="fill-[#8C8078] text-xs">No Auth</text>
            </g>
          </g>
        </svg>
      </div>

      {/* Entity Type Legend */}
      <div className="flex flex-wrap gap-4 justify-center mt-4 p-4 bg-[#F9F7F5] rounded-lg">
        {typeOrder.filter(t => entityTypes[t]).map(type => (
          <div key={type} className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: `${getEntityTypeColor(type)}30` }} />
            <span className="text-xs text-[#5D524D]">{type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClientMatrix() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [individuals, setIndividuals] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddIndividualModal, setShowAddIndividualModal] = useState(false);
  const [showAddEntityModal, setShowAddEntityModal] = useState(false);

  const fetchData = async () => {
    if (!token || !clientId) return;
    try {
      const [indRes, entRes] = await Promise.all([
        api.getIndividuals(token, clientId),
        api.getEntities(token, clientId)
      ]);
      setIndividuals(indRes.data);
      setEntities(entRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [token, clientId]);

  const handleAddIndividual = async (data) => {
    try {
      await api.createIndividual(token, clientId, data);
      toast.success('Individual added successfully');
      await fetchData();
    } catch (error) {
      console.error('Failed to add individual:', error);
      toast.error('Failed to add individual');
      throw error;
    }
  };

  const handleAddEntity = async (data) => {
    try {
      await api.createEntity(token, clientId, data);
      toast.success('Entity added successfully');
      await fetchData();
    } catch (error) {
      console.error('Failed to add entity:', error);
      toast.error('Failed to add entity');
      throw error;
    }
  };

  const handleSelectPerson = (person) => {
    navigate(`/app/clients/${clientId}/individuals`);
  };

  const handleSelectEntity = (entity) => {
    navigate(`/app/clients/${clientId}/entities`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="matrix-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Family/Entity Matrix</h1>
          <p className="text-[#5D524D]">Visual representation of family and entity relationships</p>
        </div>
      </div>

      {/* Quick Add Section */}
      <div className="caffeine-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-medium text-[#2C2420] mb-1">Add to Matrix</h3>
            <p className="text-sm text-[#8C8078]">Create new records - they will automatically appear in the visualization</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => setShowAddIndividualModal(true)}
              className="caffeine-btn"
              data-testid="matrix-add-individual-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Individual
            </Button>
            <Button 
              onClick={() => setShowAddEntityModal(true)}
              className="caffeine-btn"
              data-testid="matrix-add-entity-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entity
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="family-tree" className="w-full">
        <TabsList className="mb-6 bg-[#F0EBE6]">
          <TabsTrigger value="family-tree" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Family Tree ({individuals.length})
          </TabsTrigger>
          <TabsTrigger value="entity-relationships" className="data-[state=active]:bg-white">
            <Building2 className="w-4 h-4 mr-2" />
            Entity Relationships ({entities.length})
          </TabsTrigger>
        </TabsList>

        {/* Family Tree Tab */}
        <TabsContent value="family-tree">
          <div className="caffeine-card p-6">
            {individuals.length > 0 ? (
              <>
                <div className="text-center mb-4">
                  <h2 className="font-heading text-lg text-[#2C2420] mb-1">
                    {client?.name} - Family Structure
                  </h2>
                  <p className="text-sm text-[#8C8078]">
                    Click on any family member to view details • Green badge = corporate roles
                  </p>
                </div>

                <FamilyTreeSVG 
                  individuals={individuals} 
                  onSelectPerson={handleSelectPerson}
                />

                {/* Table Fallback */}
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm text-[#4A3B32] hover:underline">
                    View as table
                  </summary>
                  <div className="mt-4 overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Generation</th>
                          <th>Family Role</th>
                          <th>Corporate Roles</th>
                        </tr>
                      </thead>
                      <tbody>
                        {individuals.map(ind => (
                          <tr 
                            key={ind.id} 
                            onClick={() => handleSelectPerson(ind)}
                            className="cursor-pointer hover:bg-[#F9F7F5]"
                          >
                            <td className="font-medium text-[#2C2420]">
                              {ind.preferredName ? `${ind.preferredName} (${ind.firstName})` : ind.firstName} {ind.lastName}
                            </td>
                            <td className="tabular-nums">Gen {ind.generation}</td>
                            <td>{ind.familyRole || ind.role || '—'}</td>
                            <td>{ind.corporateRoles?.join(', ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-[#D9D6D0] mb-4" />
                <p className="text-lg font-medium text-[#5D524D] mb-2">No family members yet</p>
                <p className="text-sm text-[#8C8078] mb-4">Add individuals to build the family tree</p>
                <Button onClick={() => setShowAddIndividualModal(true)} className="caffeine-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Individual
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Entity Relationships Tab */}
        <TabsContent value="entity-relationships">
          <div className="caffeine-card p-6">
            {entities.length > 0 ? (
              <>
                <div className="text-center mb-4">
                  <h2 className="font-heading text-lg text-[#2C2420] mb-1">
                    {client?.name} - Entity Structure
                  </h2>
                  <p className="text-sm text-[#8C8078]">
                    Click on any entity to view details • Dashed lines = trustee relationships
                  </p>
                </div>

                <EntityRelationshipSVG 
                  entities={entities}
                  individuals={individuals}
                  onSelectEntity={handleSelectEntity}
                />

                {/* Table Fallback */}
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm text-[#4A3B32] hover:underline">
                    View as table
                  </summary>
                  <div className="mt-4 overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Entity</th>
                          <th>Type</th>
                          <th>Subtype</th>
                          <th>Directors/Trustees</th>
                          <th>Auth Set</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entities.map(entity => (
                          <tr 
                            key={entity.id}
                            onClick={() => handleSelectEntity(entity)}
                            className="cursor-pointer hover:bg-[#F9F7F5]"
                          >
                            <td className="font-medium text-[#2C2420]">{entity.name}</td>
                            <td>{entity.type}</td>
                            <td>{entity.companySubtype || entity.trustSubtype || entity.foundationStructure || '—'}</td>
                            <td>
                              {entity.directors?.map(d => d.name).join(', ') || 
                               entity.trustees?.map(t => t.name).join(', ') || '—'}
                            </td>
                            <td>
                              {entity.authoritySet ? (
                                <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" />
                              ) : (
                                <XCircle className="w-5 h-5 text-[#C25B5B]" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </>
            ) : (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 mx-auto text-[#D9D6D0] mb-4" />
                <p className="text-lg font-medium text-[#5D524D] mb-2">No entities yet</p>
                <p className="text-sm text-[#8C8078] mb-4">Add companies, trusts, or other entities</p>
                <Button onClick={() => setShowAddEntityModal(true)} className="caffeine-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Entity
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Individual Modal */}
      <AddIndividualModal
        isOpen={showAddIndividualModal}
        onClose={() => setShowAddIndividualModal(false)}
        onSave={handleAddIndividual}
        clientId={clientId}
      />

      {/* Add Entity Modal */}
      <AddEntityModal
        isOpen={showAddEntityModal}
        onClose={() => setShowAddEntityModal(false)}
        onSave={handleAddEntity}
        clientId={clientId}
        individuals={individuals}
      />
    </div>
  );
}
